import { buildFactorialIdUrl } from 'modules/core/lib/buildUrl'

/**
 * Shape of the `_factorial_id_data` cookie written by Factorial ID.  Only the
 * fields we consume are typed here; see `factorial-id/frontend/src/hooks/
 * useFactorialIdData.ts` for the full shape.
 */
interface FactorialIdCookieData {
  email?: string
}

const readFactorialIdCookie = (): FactorialIdCookieData | null => {
  const raw = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith('_factorial_id_data='))

  if (!raw) return null

  try {
    return JSON.parse(decodeURIComponent(raw.slice('_factorial_id_data='.length)))
  } catch {
    return null
  }
}

/**
 * Full-page redirect to the Factorial ID company switcher.
 *
 * Factorial ID hosts its own company selector behind its `/login` route.
 * Sending the user there (instead of the monolith
 * `/authentication/company_selector` page) is required when the session is
 * FID-issued, because:
 *
 *   - The FID session is not scoped to a monolith company, so the monolith
 *     company selector cannot meaningfully operate on it.
 *   - Changing companies within an FID session must go through the
 *     `company_selection` challenge so FID can mint a new session for the
 *     target company.
 *
 * We hit `/login` with `email`, `success=true`, and
 * `next.type=company_selection` so FID's login route hydrates straight into
 * the company-selection challenge via `createResultFromParams`, skipping
 * `getSessionNextStep()`.  Without that triplet, FID would re-check the
 * current session and \u2014 for authenticated users with a populated `access`
 * \u2014 respond with a `Redirect` challenge back to the current cell, bouncing
 * the user right back to the monolith.  This mirrors the URL-hydration
 * pattern already used by OIDC redirect flows (see `login.tsx` and
 * `api_client.ts`'s `buildRedirectUrl`).
 *
 * The email is read from the `_factorial_id_data` cookie (authored by FID
 * itself) so callers don't have to plumb it through.  It is used only for
 * display on the FID screen; the actual challenge is authorized by the FID
 * session cookies and scoped to `user.selectable_accesses` server-side.
 */
export const redirectToFactorialIdCompanySelector = (): void => {
  const email = readFactorialIdCookie()?.email ?? ''
  const params = new URLSearchParams({
    email,
    success: 'true',
    'next.type': 'company_selection',
  })
  window.location.href = `${buildFactorialIdUrl('/login')}?${params.toString()}`
}
